// [PURPOSE]: Edit an existing generated shop's metadata + re-roll items.
// [WHY]: Smoke-test 2026-05-07 surfaced the gap: "cant edit shop after
//   its made; I cant change the type of tables or item rolls after." The
//   prior shop UI was generate-only; once a shop existed, the only
//   actions were Add Item, Remove Item, Buy, and Download. To change
//   anything else (markup, size, name, item composition) the user had
//   to delete the shop and recreate it from scratch.
//
// [SCOPE]: This dialog edits non-destructive metadata (name, shopkeeper,
//   markup, size) AND offers a "Re-roll Items" action that replaces the
//   item list using the SAME shop type the shop was built from. Changing
//   the shop type itself remains a separate flow (Custom Shop Type
//   Creator) because it requires roll-table re-binding.
//
// [INTEGRATION]: Mounted by App.jsx, controlled via UIContext
//   (`showEditShopDialog`, `editingShop`). On save, mutates the matching
//   shop in `activeShops` and `generatedShop` (when the active one) by
//   id. Re-roll dispatches to a `regenerateShop` callback wired by
//   App.jsx so the d100 + simple-mode flows stay co-located with the
//   rest of the shop generation logic.

import React, { useState, useEffect, useCallback } from 'react';
import MobileModal from './MobileModal';
import { useConfirm } from '../contexts/ConfirmContext';
// [PHASE B / #058 — 2026-05-07]: Shop-type group list — shared with
//   ShopTab's Generate-form dropdown. Lets the user swap a shop's
//   bound shopType from this dialog so they don't have to re-create
//   the shop just to change its tables.
import { SHOP_TYPE_GROUPS } from '../data/shopTypeGroups';

const SHOP_SIZES = [
  { value: 'small', label: 'Small (~10 items)' },
  { value: 'medium', label: 'Medium (~15 items)' },
  { value: 'large', label: 'Large (~20 items)' },
  { value: 'massive', label: 'Massive (~30 items)' },
];

export default function EditShopDialog({
  open,
  shop,
  customShopTypes = [],
  onClose,
  onSave,
  onReroll,
  // [PHASE B / #059 — 2026-05-07]: When the shop's bound type is a
  //   user-authored customShopTypes entry, we surface an "Edit Type
  //   Bindings" button. Clicking it closes this dialog and opens the
  //   Custom Shop Type Creator pre-filled with that type's existing
  //   values. App.jsx wires the cascade.
  onEditType,
}) {
  const [shopName, setShopName] = useState('');
  const [shopkeeper, setShopkeeper] = useState('');
  const [markup, setMarkup] = useState(100);
  const [shopSize, setShopSize] = useState('medium');
  const [shopType, setShopType] = useState('general');
  const confirm = useConfirm();

  // Sync form state with the shop being edited whenever it changes
  // (open transitions from false → true, or shop swap).
  useEffect(() => {
    if (open && shop) {
      setShopName(shop.shopName || '');
      setShopkeeper(shop.shopkeeper || '');
      setMarkup(typeof shop.markup === 'number' ? shop.markup : 100);
      setShopSize(shop.shopSize || 'medium');
      setShopType(shop.shopType || 'general');
    }
  }, [open, shop]);

  const handleSave = useCallback(() => {
    if (!shop) return;
    if (!shopName.trim()) {
      // eslint-disable-next-line no-alert
      window.alert('Shop name is required');
      return;
    }
    if (!shopkeeper.trim()) {
      // eslint-disable-next-line no-alert
      window.alert('Shopkeeper name is required');
      return;
    }
    const markupNum = parseInt(markup, 10);
    if (!Number.isFinite(markupNum) || markupNum < 1) {
      // eslint-disable-next-line no-alert
      window.alert('Markup must be a positive number (e.g., 100 = no markup)');
      return;
    }
    onSave({
      ...shop,
      shopName: shopName.trim(),
      shopkeeper: shopkeeper.trim(),
      markup: markupNum,
      shopSize,
      shopType,
    });
  }, [shop, shopName, shopkeeper, markup, shopSize, shopType, onSave]);

  // [UI-CONFIRM-UNIFICATION 2026-07-04]: styled async confirm replaces window.confirm.
  const handleReroll = useCallback(async () => {
    if (!shop) return;
    const ok = await confirm({
      title: 'Re-roll Items',
      message: 'Re-roll all items in this shop? Current items will be replaced.',
      confirmText: 'Re-roll',
      confirmColor: 'red',
    });
    if (!ok) return;
    onReroll({
      ...shop,
      shopName: shopName.trim() || shop.shopName,
      shopkeeper: shopkeeper.trim() || shop.shopkeeper,
      markup: parseInt(markup, 10) || shop.markup,
      shopSize,
      shopType,
    });
  }, [shop, shopName, shopkeeper, markup, shopSize, shopType, onReroll, confirm]);

  if (!open || !shop) return null;

  const isInventoryShop = shop.source === 'player_inventory';

  return (
    <MobileModal
      isOpen={open}
      onClose={onClose}
      title="✏️ Edit Shop"
      onSave={handleSave}
      saveLabel="Save Changes"
      overlayClassName="z-[100]"
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="edit-shop-name" className="block text-sm text-gray-300 mb-1">
            Shop Name
          </label>
          <input
            id="edit-shop-name"
            type="text"
            value={shopName}
            onChange={(e) => setShopName(e.target.value)}
            placeholder="The Rusty Anvil"
            className="w-full p-2 bg-gray-700 text-white rounded"
          />
        </div>

        <div>
          <label htmlFor="edit-shop-keeper" className="block text-sm text-gray-300 mb-1">
            Shopkeeper
          </label>
          <input
            id="edit-shop-keeper"
            type="text"
            value={shopkeeper}
            onChange={(e) => setShopkeeper(e.target.value)}
            placeholder="Bartholomew the Blacksmith"
            className="w-full p-2 bg-gray-700 text-white rounded"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="edit-shop-markup" className="block text-sm text-gray-300 mb-1">
              Markup % <span className="text-xs text-gray-400">(100 = no markup)</span>
            </label>
            <input
              id="edit-shop-markup"
              type="number"
              min="1"
              value={markup}
              onChange={(e) => setMarkup(e.target.value)}
              className="w-full p-2 bg-gray-700 text-white rounded"
            />
          </div>

          <div>
            <label htmlFor="edit-shop-size" className="block text-sm text-gray-300 mb-1">
              Shop Size
            </label>
            <select
              id="edit-shop-size"
              value={shopSize}
              onChange={(e) => setShopSize(e.target.value)}
              className="w-full p-2 bg-gray-700 text-white rounded"
              disabled={isInventoryShop}
            >
              {SHOP_SIZES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* [PHASE B / #058 — 2026-05-07]: Shop type swap. Pre-#058
            the dialog showed the bound type as a read-only code chip;
            users had to delete the shop and create a new one to change
            its bound tables. Now editable. Inventory-source shops
            keep the static label since they don't roll from tables. */}
        {isInventoryShop ? (
          <div className="bg-gray-700 p-3 rounded text-sm">
            <div className="text-gray-300 font-bold mb-1">Shop Type</div>
            <div className="text-gray-400 text-xs">
              📦 Player Inventory (re-roll re-snapshots your current inventory)
            </div>
          </div>
        ) : (
          <div>
            <label htmlFor="edit-shop-type" className="block text-sm text-gray-300 mb-1">
              Shop Type
            </label>
            <select
              id="edit-shop-type"
              value={shopType}
              onChange={(e) => setShopType(e.target.value)}
              className="w-full p-2 bg-gray-700 text-white rounded"
            >
              {SHOP_TYPE_GROUPS.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </optgroup>
              ))}
              {customShopTypes.length > 0 && (
                <optgroup label="✨ Your Custom Shop Types">
                  {customShopTypes.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.icon || '🏪'} {s.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            {shopType !== shop.shopType && (
              <div className="text-xs text-amber-300 mt-1">
                💡 Click <strong>Re-roll Items</strong> below to refresh the shop with this type's tables.
                Saving without re-rolling keeps the current items.
              </div>
            )}
            {/* [PHASE B / #059 — 2026-05-07]: Edit-type-bindings affordance.
                Visible only when the currently-selected shopType is one
                of the user's custom shop types. Routes the user to the
                Custom Shop Type Creator in edit mode so they can change
                which roll tables (or simple-mode categories) the type
                is bound to. */}
            {customShopTypes.some((s) => s.id === shopType) && onEditType && (
              <button
                type="button"
                onClick={() => {
                  const target = customShopTypes.find((s) => s.id === shopType);
                  if (target) onEditType(target);
                }}
                className="mt-2 w-full py-2 bg-purple-800 hover:bg-purple-700 rounded text-sm font-bold"
              >
                ✏️ Edit Bindings of "{customShopTypes.find((s) => s.id === shopType)?.name}"
              </button>
            )}
          </div>
        )}

        <div className="bg-amber-900 bg-opacity-30 border border-amber-700 p-3 rounded">
          <div className="text-sm font-bold text-amber-300 mb-1">🎲 Re-roll Items</div>
          <p className="text-xs text-amber-200 mb-2">
            Replace the current items with a fresh roll using this shop's type and size.
            Useful after changing markup or size, or to refresh an existing shop's inventory.
          </p>
          <button
            type="button"
            onClick={handleReroll}
            className="w-full py-2 bg-amber-700 hover:bg-amber-600 rounded text-sm font-bold"
          >
            🎲 Re-roll Items in this Shop
          </button>
        </div>
      </div>
    </MobileModal>
  );
}
